import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type Ref,
  type RefObject,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useAppDispatch, useAppSelector } from "@/store/app/hooks";
import {
  selectTextEditorMode,
  setTextEditorMode,
  type TextEditorMode,
} from "@/features/sparxsEditor/store/slices/session/sessionPreferencesSlice";
import { htmlToMarkdown } from "@/lib/markdown/sparxMarkdownHtml";
import {
  markdownToPlainDoc,
  plainDocToMarkdown,
} from "@/lib/markdown/markdownPlainDoc";
import { markdownToTiptapDocJSON } from "./markdownToTiptapDoc";
import { cn } from "@/lib/utils";
import {
  DEFAULT_TOOLBAR_BUTTONS,
  type ToolbarButtonId,
} from "./toolbar/shared/richTextEditorToolbarConstants";
import {
  RteFeature,
  featureBlacklistHas,
  matchesMinimalFeatureBlacklist,
  toolbarButtonsForFeatureBlacklist,
} from "./richTextEditorFeatureBlacklist";
import { RichTextEditorHeader } from "./toolbar/header/richTextEditorHeader";
import { RichTextEditorFooter } from "./toolbar/footer/richTextEditorFooter";
import { BubbleMenuToolbar } from "./toolbar/bubbleMenu/richTextEditorBubbleMenuToolbar";
import { useEditorContentRevision } from "@/features/sparxsEditor/contexts/EditorContentRevisionContext";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import {
  buildWysiwygExtensionsWithBlacklist,
  MARKDOWN_EXTENSIONS,
} from "./richTextEditorExtensions";
import {
  BUBBLE_MENU_CLASS,
  contentAreaLayoutClassForVariant,
  editorProseMirrorClassForVariant,
  RICH_TEXT_EDITOR_ROOT_CLASS,
  type RichTextEditorContentAreaVariant,
} from "./richTextEditorLayout";

const EMPTY_VARIABLES: Variables[] = [];

export interface RichTextEditorRef {
  insertVariableToken: (rawToken: string) => void;
}

export interface RichTextEditorProps {
  id: string;
  initialValue: string;
  onChange: (markdown: string) => void;
  onBlur?: () => void;
  className?: string;

  ariaInvalid?: boolean;

  ariaDescribedBy?: string;

  editorRef?:
    | RefObject<RichTextEditorRef | null>
    | Ref<RichTextEditorRef | null>;
  buttons?: ToolbarButtonId[];
  variables?: Variables[];
  /** When true, the editor is read-only and chrome (toolbar, mode tabs) is hidden. */
  disabled?: boolean;
  /** CTA fields use a single-line-tall content box; default keeps ~3–4 lines. */
  contentAreaVariant?: RichTextEditorContentAreaVariant;
  /** When non-empty, disables mapped toolbar/extensions/chrome features (see presets in richTextEditorFeatureBlacklist). */
  featureBlacklist?: readonly RteFeature[];
}

export const RichTextEditor = ({
  id,
  initialValue,
  onChange,
  onBlur,
  className,
  ariaInvalid,
  ariaDescribedBy,
  editorRef,
  buttons = DEFAULT_TOOLBAR_BUTTONS,
  variables = EMPTY_VARIABLES,
  disabled = false,
  contentAreaVariant = "default",
  featureBlacklist,
}: RichTextEditorProps) => {
  const dispatch = useAppDispatch();
  const resetRevision = useEditorContentRevision();
  const lastAppliedRevisionRef = useRef(resetRevision);
  const mode = useAppSelector(selectTextEditorMode);
  const forceWysiwygForBlacklist =
    matchesMinimalFeatureBlacklist(featureBlacklist);
  const isWysiwygFromStore = mode === "wysiwyg";
  const effectiveIsWysiwyg = forceWysiwygForBlacklist || isWysiwygFromStore;

  const hideBubbleMenu = featureBlacklistHas(
    featureBlacklist,
    RteFeature.bubbleMenu,
  );
  const hideEditorModeTabs = featureBlacklistHas(
    featureBlacklist,
    RteFeature.editorModeTabs,
  );
  const hideHeaderTrailingHelp = featureBlacklistHas(
    featureBlacklist,
    RteFeature.headerTrailingHelp,
  );

  const resolvedButtons = useMemo((): ToolbarButtonId[] => {
    if (featureBlacklist == null || featureBlacklist.length === 0) {
      return buttons;
    }
    return toolbarButtonsForFeatureBlacklist(featureBlacklist);
  }, [featureBlacklist, buttons]);

  const emitHorizontalRules = !featureBlacklistHas(
    featureBlacklist,
    RteFeature.horizontalRule,
  );

  const contentAreaLayoutClass =
    contentAreaLayoutClassForVariant(contentAreaVariant);

  const editorProseMirrorClass = useMemo(
    () => editorProseMirrorClassForVariant(contentAreaVariant),
    [contentAreaVariant],
  );

  const variablesKey = useMemo(() => JSON.stringify(variables), [variables]);
  const wysiwygExtensions = useMemo(() => {
    const parsedVariables = JSON.parse(variablesKey) as Variables[];
    return buildWysiwygExtensionsWithBlacklist(
      parsedVariables,
      featureBlacklist ?? [],
    );
  }, [variablesKey, featureBlacklist]);

  const editor = useEditor(
    {
      extensions: effectiveIsWysiwyg ? wysiwygExtensions : MARKDOWN_EXTENSIONS,
      content: effectiveIsWysiwyg
        ? markdownToTiptapDocJSON(initialValue, { emitHorizontalRules })
        : markdownToPlainDoc(initialValue),
      editable: !disabled,
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      editorProps: {
        attributes: {
          class: editorProseMirrorClass,
        },
        handleDOMEvents: {
          blur: () => {
            onBlur?.();
          },
        },
      },
      onUpdate: ({ editor: e }) => {
        if (effectiveIsWysiwyg) {
          onChange(htmlToMarkdown(e.getHTML()));
        } else {
          onChange(plainDocToMarkdown(e.state.doc));
        }
      },
    },
    [effectiveIsWysiwyg, wysiwygExtensions, disabled, editorProseMirrorClass],
  );

  useEffect(() => {
    if (editor == null) return;
    if (resetRevision === lastAppliedRevisionRef.current) return;
    lastAppliedRevisionRef.current = resetRevision;
    const content = effectiveIsWysiwyg
      ? markdownToTiptapDocJSON(initialValue, { emitHorizontalRules })
      : markdownToPlainDoc(initialValue);
    editor.commands.setContent(content, { emitUpdate: false });
  }, [
    editor,
    initialValue,
    effectiveIsWysiwyg,
    resetRevision,
    emitHorizontalRules,
  ]);

  useImperativeHandle(
    editorRef,
    () => ({
      insertVariableToken: (rawToken: string) => {
        if (editor == null) return;
        if (effectiveIsWysiwyg) {
          editor
            .chain()
            .focus()
            .insertContent({ type: "variableToken", attrs: { raw: rawToken } })
            .run();
          onChange(htmlToMarkdown(editor.getHTML()));
        } else {
          const text = `{{${rawToken}}}`;
          editor.chain().focus().insertContent(text).run();
          onChange(plainDocToMarkdown(editor.state.doc));
        }
      },
    }),
    [editor, onChange, effectiveIsWysiwyg],
  );

  const setMode = useCallback(
    (next: TextEditorMode) => {
      dispatch(setTextEditorMode(next));
    },
    [dispatch],
  );

  const handleModeValueChange = useCallback(
    (v: string) => {
      if (v === "markdown" || v === "wysiwyg") setMode(v);
    },
    [setMode],
  );

  if (editor == null) return null;

  const editorBody = (
    <div
      id={id}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      className={cn("rounded-none border-0", contentAreaLayoutClass)}
    >
      <EditorContent editor={editor} />
      {!disabled && effectiveIsWysiwyg && !hideBubbleMenu && (
        <BubbleMenu
          editor={editor}
          shouldShow={({ state }) => {
            const { from, to } = state.selection;
            return from < to;
          }}
          options={{ placement: "top" }}
          className={BUBBLE_MENU_CLASS}
        >
          <BubbleMenuToolbar
            editor={editor}
            featureBlacklist={featureBlacklist}
          />
        </BubbleMenu>
      )}
    </div>
  );

  return (
    <div className={cn(RICH_TEXT_EDITOR_ROOT_CLASS, className)}>
      {!disabled && (
        <RichTextEditorHeader
          editor={editor}
          isWysiwyg={effectiveIsWysiwyg}
          buttons={resolvedButtons}
          variables={variables ?? EMPTY_VARIABLES}
          onChange={onChange}
          showTrailingHelp={!hideHeaderTrailingHelp}
        />
      )}
      {disabled ? (
        editorBody
      ) : hideEditorModeTabs ? (
        editorBody
      ) : (
        <RichTextEditorFooter
          value={effectiveIsWysiwyg ? "wysiwyg" : "markdown"}
          onValueChange={handleModeValueChange}
          ariaLabel="Editor mode"
        >
          {editorBody}
        </RichTextEditorFooter>
      )}
    </div>
  );
};
